import { Link } from 'react-router-dom';

import { Avatar, Icon, IconButton, Popover, Typography } from '@/ui';

import { formatIsoDateTimeRange } from './calendarGrid';
import {
  recordCanCancel,
  recordCanReschedule,
  subjectActionRecord,
} from './recordActionGate';

import type { CalendarActionSubject } from '@/features/appointments/calendarAppointmentProjection';
import type { Session } from '@/types';
import type { FC } from 'react';

export interface SessionInfoPopoverProps {
  adminMode?: boolean;
  session: Session | null;
  subject?: CalendarActionSubject;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onReschedule: (
    subject: CalendarActionSubject | Session,
    returnFocus: () => void,
  ) => void;
  onCancel: (subject: CalendarActionSubject | Session) => void;
  timeZone?: string | null;
}

// MEN-4239: the Figma popover frame is a 2x export, so its 556x442 pixels
// are a 279x224 card: 16px padding, a right-aligned pencil/trash/close row,
// a 16px bold title over a 14px date line, then avatar + linked-name rows.
const SESSION_POPOVER_TITLE_CLASS =
  'text-base font-bold leading-6 tracking-[0.15px] text-cement-600';
const SESSION_POPOVER_META_CLASS =
  'text-sm leading-5 tracking-[0.15px] text-cement-600';
const SESSION_POPOVER_PARTICIPANT_CLASS = 'flex items-center gap-3';
const SESSION_POPOVER_PARTICIPANT_NAME_CLASS =
  'text-sm leading-5 tracking-[0.15px] text-sky-500';

// Shared info popover for the Week/Month views (MEN-4160). Upcoming sessions
// expose reschedule (pencil) and cancel (trash); past sessions are read-only.
const SessionInfoPopover: FC<SessionInfoPopoverProps> = ({
  adminMode = false,
  anchorEl,
  onCancel,
  onClose,
  onReschedule,
  session,
  subject,
  timeZone,
}) => {
  if (!session) return null;

  // Past only once ended — in-progress active/rescheduled sessions keep actions.
  const isPast = new Date(session.endedAt).getTime() < Date.now();
  // MEN-4122: gate on the action record, exactly as the List menu does. For a
  // native Appointment that additionally requires `lifecycleStatus ===
  // 'scheduled'`, so Week/Month stop offering a second command against an
  // Appointment whose cancellation or reschedule is already in flight. A
  // legacy Rails row has no record to consult and still answers as a Session.
  const record = subject ? subjectActionRecord(subject) : session;
  const canReschedule = !isPast && recordCanReschedule(record, adminMode);
  const canCancel = !isPast && recordCanCancel(record, adminMode);

  // MEN-4239: Tailwind utilities in this app are scoped under
  // `:is(#main, #storybook-root, #storybook-docs)` (the `important` option in
  // `tailwind.config.cjs`, which pairs with preflight being off). MUI portals
  // a Popover to `<body>` by default, landing it outside that scope so every
  // utility class on the card silently does nothing. Portalling into the
  // anchor's own styling root keeps the card styled while still escaping the
  // calendar viewport's `overflow` clipping. Falls back to the MUI default
  // when there is no such root (jsdom tests anchor on `document.body`).
  const stylingRoot =
    anchorEl?.closest<HTMLElement>('#main, #storybook-root, #storybook-docs') ??
    undefined;

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      container={stylingRoot}
      open={Boolean(anchorEl)}
      slotProps={{
        paper: {
          className: 'rounded-xl bg-white shadow-[0_6px_18px_rgba(0,0,0,0.16)]',
        },
      }}
      onClose={onClose}
    >
      <div
        className="flex flex-col p-4"
        data-testid="session-info-popover"
        style={{ height: '224px', width: '279px' }}
      >
        <div className="mb-1 flex items-center justify-end gap-1">
          {canReschedule && (
            <IconButton
              aria-label="Reschedule session"
              size="small"
              onClick={() =>
                onReschedule(subject ?? session, () => anchorEl?.focus())
              }
            >
              <Icon>edit</Icon>
            </IconButton>
          )}
          {canCancel && (
            <IconButton
              aria-label="Cancel session"
              size="small"
              onClick={() => onCancel(subject ?? session)}
            >
              <Icon>trash</Icon>
            </IconButton>
          )}
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </div>

        <Typography
          className={SESSION_POPOVER_TITLE_CLASS}
          component="h2"
          variant="subtitle1"
        >
          {session.title}
        </Typography>
        <Typography className={SESSION_POPOVER_META_CLASS} variant="body2">
          {formatIsoDateTimeRange(session.startedAt, session.endedAt, timeZone)}
        </Typography>

        <div
          className="mt-3 flex flex-col gap-2"
          data-testid="session-info-participants"
        >
          {session.coach.fullName && (
            <div className={SESSION_POPOVER_PARTICIPANT_CLASS}>
              <Avatar
                firstName={session.coach.firstName}
                lastName={session.coach.lastName}
                size="s"
                src={session.coach.avatarUrl}
              />
              <Typography
                className={SESSION_POPOVER_PARTICIPANT_NAME_CLASS}
                variant="body2"
              >
                {session.coach.fullName}
              </Typography>
            </div>
          )}
          {session.user.fullName &&
            (session.user.id ? (
              <Link
                aria-label={`View profile for ${session.user.fullName}`}
                className={`${SESSION_POPOVER_PARTICIPANT_CLASS} no-underline`}
                rel="noopener noreferrer"
                target="_blank"
                to={`/coach/members/${encodeURIComponent(session.user.id)}`}
              >
                <Avatar
                  firstName={session.user.firstName}
                  lastName={session.user.lastName}
                  size="s"
                  src={session.user.avatarUrl}
                />
                <Typography
                  className={SESSION_POPOVER_PARTICIPANT_NAME_CLASS}
                  variant="body2"
                >
                  {session.user.fullName}
                </Typography>
              </Link>
            ) : (
              <div className={SESSION_POPOVER_PARTICIPANT_CLASS}>
                <Avatar
                  firstName={session.user.firstName}
                  lastName={session.user.lastName}
                  size="s"
                  src={session.user.avatarUrl}
                />
                <Typography
                  className={SESSION_POPOVER_PARTICIPANT_NAME_CLASS}
                  variant="body2"
                >
                  {session.user.fullName}
                </Typography>
              </div>
            ))}
        </div>
      </div>
    </Popover>
  );
};

export default SessionInfoPopover;
